/**
 * AddToCartFeedback Component
 * 
 * Slide-in panel that appears when an item is added to cart.
 * Shows the added item with image, provides quick actions.
 */

import React, { useEffect, useRef } from 'react';
import { ShopifyProduct, ShopifyProductVariant } from '../../types/shopify';
import { useShoppingCart } from '../../hooks/useShoppingCart';
import { useShoppingCartModal } from '../../contexts/ShoppingCartModalContext';
import OptimizedContentImage from '../common/OptimizedContentImage';
import { formatPrice, resolveVariantPrice } from '../../utils/shopifyHelpers';
import { SHOW_FREE_SHIPPING_PROGRESS } from '../../config/featureFlags';

interface AddToCartFeedbackProps {
  isOpen: boolean;
  onClose: () => void;
  product: ShopifyProduct | null;
  variant: ShopifyProductVariant | null;
  quantity: number;
  triggerCount?: number;
}

const AddToCartFeedback: React.FC<AddToCartFeedbackProps> = ({
  isOpen,
  onClose,
  product,
  variant,
  quantity,
  triggerCount,
}) => {
  const { totals } = useShoppingCart();
  const { openCart } = useShoppingCartModal();
  const panelRef = useRef<HTMLDivElement>(null);

  // Default totals if not available
  const safeTotals = totals || { itemCount: 0, subtotal: 0, currency: 'GBP' };

  // Auto-close after 5 seconds - reset timer on any new add (triggerCount changes)
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [isOpen, onClose, triggerCount]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleViewCart = () => {
    onClose();
    openCart();
  };

  if (!isOpen || !product || !variant) return null;

  const productImage = product.images.edges[0]?.node;
  const pricing = resolveVariantPrice(variant, product);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-in Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Item added to cart"
        className="fixed top-0 right-0 h-auto max-h-[90vh] w-full sm:w-96 bg-gray-900 border-l border-gray-700 shadow-2xl z-50 animate-slide-in-right"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700 bg-green-900/20">
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-medium text-white">Added to cart</span>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-1"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Added Item */}
        <div className="p-4">
          <div className="flex space-x-4">
            {/* Product Image */}
            <div className="flex-shrink-0 w-20 h-20 bg-gray-800 rounded-lg overflow-hidden">
              {productImage ? (
                <OptimizedContentImage
                  src={productImage.url}
                  alt={productImage.altText || product.title}
                  className="w-full h-full"
                  imageClassName="w-full h-full object-cover"
                  sizes="80px"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-500">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
            </div>

            {/* Product Details */}
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-white truncate">{product.title}</h3>
              {variant.title !== 'Default Title' && (
                <p className="text-xs text-gray-400 mt-1">{variant.title}</p>
              )}
              <div className="mt-2 flex items-center justify-between">
                <span className="text-sm text-gray-300">Qty: {quantity}</span>
                <span className="text-sm font-medium text-white">
                  {formatPrice(pricing.amount * quantity, pricing.currencyCode)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Cart Summary */}
        <div className="px-4 py-3 bg-gray-800/50 border-t border-gray-700">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-300">
              Cart subtotal ({safeTotals.itemCount} {safeTotals.itemCount === 1 ? 'item' : 'items'})
            </span>
            <span className="font-medium text-white">
              {formatPrice(safeTotals.subtotal, safeTotals.currency)}
            </span>
          </div>
          
          {/* Free shipping progress - behind feature flag due to complex shipping rules */}
          {SHOW_FREE_SHIPPING_PROGRESS ? (
            <>
              {safeTotals.subtotal < 50 && (
                <div className="mt-2">
                  <p className="text-xs text-gray-400">
                    Add {formatPrice(50 - safeTotals.subtotal, safeTotals.currency)} more for free shipping
                  </p>
                  <div className="mt-1 w-full bg-gray-700 rounded-full h-1.5">
                    <div
                      className="h-1.5 rounded-full bg-red-600 transition-all"
                      style={{ width: `${Math.min((safeTotals.subtotal / 50.01) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              )}
              {safeTotals.subtotal >= 50 && (
                <p className="mt-1 text-xs text-green-400">✓ Free shipping unlocked</p>
              )}
            </>
          ) : (
            <p className="mt-2 text-xs text-gray-400">Shipping calculated at checkout</p>
          )}
        </div>

        {/* Actions */}
        <div className="p-4 space-y-3 border-t border-gray-700">
          <button
            onClick={handleViewCart}
            className="w-full py-3 bg-red-900 hover:bg-red-800 text-white font-medium rounded-lg transition-colors"
          >
            View Cart & Checkout
          </button>
          <button
            onClick={onClose}
            className="w-full py-2 text-sm text-gray-300 hover:text-white transition-colors"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    </>
  );
};

export default AddToCartFeedback;
